import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AlertModel, CLOSED_OR_RESOLVED_STATUS_VALUES } from './alert.model';
import { AlertRcaReportRequest, AlertRcaReportResponse, AlertRcaReportStatus } from './alert-rca-report.model';
import { SystemDetailsService } from '../system/system-details.service';
import { UserService } from '../auth/_services/user.service';
import { catchError, map, tap } from 'rxjs/operators';
import { DateAgoPipe } from '../../views/pages/pipes/date-ago.pipe';
import { ReplaceNullValuePipe } from '../../views/pages/pipes/replace-null-value.pipe';
import * as moment from 'moment';
import { AlertViewModel } from './alert-view.model';
import { SplitMergeConflictEvent, SplitMergeConflictService } from './split-merge-conflict.service';
const API_ALERT_URL = '/api/alert';
const PREDICTION_FEEDBACK = "/prediction/feedbackalerts";
const API_URL = environment.API_URL;
const API_SCADA_URL = '/api/scada'
const API_ANALYSIS_ALERT_URL = '/api/analysis/alert';
const API_ALERT_MONETARY_VALUE_URL = '/api/alert/update-monetary-values';
const API_ALERT_FAILURE_VISBILITY_URL = '/api/alert/update-failure-visibility';
const FEEDBACK_URL = '/genai/feedback';

@Injectable({
  providedIn: 'root'
})
export class AlertService {

  alertList: any = [];
  alertTypeChanged = new Subject<any>();
  maintenanceDataUpdated = new Subject<any>();
  alertListChanged = new BehaviorSubject<any>({data: []});
  newlyAddedTag: string[] = [];
  removedTagsFromGroupedTagChart: string[] = [];
  alertListDataChanged = new EventEmitter<any>();
  alertRangeListChanged = new EventEmitter<any[]>();
  alertDetailsChanged = new EventEmitter<any>();
  alertFailureTimelineChanged = new EventEmitter<any>();
  failureTemplateIdsChanged = new EventEmitter<any>();
  updateAlertSubscription = new EventEmitter<any>();
  failureModeList = []
  dqAlerts = []
  thresholdAlerts = []
  failureModeSelected = new EventEmitter<any>();
  loading: boolean = false;
  alertFailureChanged = new EventEmitter<any>();
  alertOwnerChanged = new EventEmitter<any[]>();
  onOpTagSelection = new EventEmitter<any[]>();
  onAlertRCARootCauseHypothesisSelection = new EventEmitter<any[]>();
  onFilterChange = new EventEmitter<any>();
  systemChange = new EventEmitter<any>();
  tagGraphsDataChanged = new EventEmitter<any>();
  alertFailureSapNotificationChanged = new EventEmitter<any>();
  graphAxisChanged = new EventEmitter<any>();
  tagGraphClicked = new EventEmitter<any>();
  rcaGenerationInProgress: boolean = false;
  originalFailures: any;
  selectedFailures: any;
  timeLineLoading: boolean;
  selectedSystem: any;
  lastMaintenanceData = [];
  lastUsedAlertListURL: string;
  PYTHON_API_URL;
  alertViewUrl = `${API_URL}/api/alert/view`;
  constructor(
    private readonly http: HttpClient,
    private readonly systemDetailsService: SystemDetailsService,
    private readonly userService: UserService,
    private readonly dateAgoPipe: DateAgoPipe,
    private readonly replaceNullValuePipe: ReplaceNullValuePipe,
    private readonly splitMergeConflictService: SplitMergeConflictService,
  ) {
    this.systemChange.subscribe(sys => this.selectedSystem = sys)
    this.PYTHON_API_URL = environment.runtime.PYTHON_API_URL;
    this.splitMergeConflict$ = this.splitMergeConflictService.conflict$;
   }

  // Emits a SplitMergeConflictEvent whose updateAlert call was rejected with a
  // SPLIT_MERGE_IN_PROGRESS 409, or whose split/merge API itself returned 202
  // (queued) or 409 (already in progress). Listeners flip their local copy of
  // the alert into the in-progress state, revert any optimistic writes, and
  // start polling for the backend to clear the flag.
  readonly splitMergeConflict$: Observable<SplitMergeConflictEvent>;

  private alertCategoryMap: { [key: number]: string } = {
    1: 'UnScheduled Maintenance',
    2: 'Scheduled Maintenance',
    3: 'Force Majeure',
    4: 'EGBD',
    5: 'IGBD',
  };


  matchAlertRange(alert, dateRange) {
    return (!dateRange.from && !dateRange.to) || (new Date(alert.range.from) > new Date(dateRange.from) && new Date(alert.range.from) < new Date(dateRange.to))
    || !alert.range.to
    || (new Date(alert.range.to) > new Date(dateRange.from) && new Date(alert.range.to) < new Date(dateRange.to))
  }

  getScadaFilter() {
    let url = `${API_URL}${API_ALERT_URL}/scada-filter`
    return this.http.get(url,{ headers: this.userService.getHttpAuthHeader() })
  }

  updateScadaSetCause(d) {
    let url = `${API_URL}${API_SCADA_URL}`
    return this.http.put(url, d, { headers: this.userService.getHttpAuthHeader() })
  }

  getAlertList(d, type = 0, getPaginatedData = true, observable = false, fields = null, fromAlertListPage = false) {
    this.loading = false
    // {page=0, limit=0, from, to, filter={}}
    let url = `${API_URL}${API_ALERT_URL}/list/${d.page}/${d.limit}`;
    delete d.page;
    delete d.limit;
    if (fields){
      d.fields = fields;
    }
    let params = new HttpParams();
    if (observable)
      return this.http.post(url, d, { headers: this.userService.getHttpAuthHeader(), params });

    this.http.post(url, d, { headers: this.userService.getHttpAuthHeader(), params}).subscribe((data: any) => {
      if(type==5)
        this.thresholdAlerts = [...data.data];
      else if(type==2)
        this.dqAlerts = [...data.data];
      else {
        this.loading = true
        if (fromAlertListPage)
          this.alertListDataChanged.emit({ ...data });
        else
          this.alertListChanged.next({ ...data });
      }
    }, (error) => {
      this.loading = true
      if (fromAlertListPage)
        this.alertListDataChanged.emit(this.alertList);
      else
        this.alertListChanged.next(this.alertList);
    });
  }
  getAlertRangeList(d) {
    this.loading = false
    let url = `${API_URL}${API_ALERT_URL}/range`;
    this.http.post(url, d, { headers: this.userService.getHttpAuthHeader() }).subscribe((data: any) => {
      this.loading = true
      this.alertRangeListChanged.emit({ ...data });
    }, (error) => {
      this.loading = true
      let alertRangeList: any = { data: [] };
      this.alertRangeListChanged.emit({ ...alertRangeList });
    });
  }

  submitRCAFeedback(run_id: string, feedback: number, description: string = '', type: string = 'rca'): Observable<Object> {
    const url = `${this.PYTHON_API_URL}${FEEDBACK_URL}`;
    const headers = this.userService.getHttpAuthHeader();
    const body = {
      run_id,
      feedback,
      description,
      type
    };
    return this.http.post(url, body, { headers });
  }


  downloadAlertList(d) {
    let url = `${API_URL}${API_ALERT_URL}/list/${d.page}/${d.limit}`;
    url = url + "?csv=true"
    delete d.page;
    delete d.limit;
    d.alertDetailWebEndpoint = '/web/alert/detail'
    const type = d.filter.type?.[0];
    this.http.post(url, d, { headers: this.userService.getHttpAuthHeader(), responseType: 'text' }).subscribe((res) => {
      const a = document.createElement("a");
      //a.href = 'data:attachment/csv;charset=utf-8,' + encodeURI(res);
      const blob = new Blob([res], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob)
      a.setAttribute('href', url)
      let prefix;
      switch (type) {
        case 1:
          prefix = "AnomalyAlerts_";
          break;
        case 2:
          prefix = "DQAlerts_";
          break;
        case 4:
          prefix = "DiagnosisAlerts_";
          break;
        case 5:
          prefix = "ThresholdAlerts_";
          break;
        default:
          prefix = "Alerts_";
          break;
      }
      a.download = prefix + new Date().toISOString().split("T")[0] + ".csv";
      a.click();
    })
  }

  getAlertDetails(_id, observable = false, fields = null, populateFailureSapNotif = false) {
    let url = `${API_URL}${API_ALERT_URL}/${_id}`;

    let params = new HttpParams()
      .set('_id', _id)
      .set('populateFailureSapNotif', populateFailureSapNotif);

    if(fields){
      params = params.set('fields', fields);
    }

    if (observable) {
      return this.http.get(url, {
        headers: this.userService.getHttpAuthHeader(),
        params
      });
    }

    this.http.get(url, {
      headers: this.userService.getHttpAuthHeader(),
      params
    }).subscribe((data: any) => {
      this.alertDetailsChanged.emit(data.data);
    });
  }

  getAlertprediction(_id, unit): Observable<any> {
    let url = `${API_URL}/api/prediction/alert_root_cause_recommendation`;
    let params = new HttpParams().set('unit', unit).set('alert_id', _id);
    return this.http.get(url, { params, headers: this.userService.getHttpAuthHeader() }).pipe(
      map((data: any) => {
        if (data.status == 200) {
          return data.data;
        }
        return [];
      }),
      catchError(error => {
        console.error('Error:', error);
        return [];
      })
    );
  }

  getAlertFailureTimeLine(_id: any, alertId: any, system?: string) {
    this.timeLineLoading = true;
    let params = new HttpParams()
      .set('_id', _id)
      .set('alertId', alertId);
    if (system) {
      params = params.set('system', system);
    }
    const url = `${API_URL}${API_ALERT_URL}/failure/timeline`;
    this.http.get(url, { headers: this.userService.getHttpAuthHeader(), params }).subscribe({
      next: (data: any) => {
        this.alertFailureTimelineChanged.emit(data);
        this.timeLineLoading = false;
      },
      error: (error) => {
        this.alertFailureTimelineChanged.emit({ data: [] });
        this.timeLineLoading = false;
        console.log(error.status, error.error?.message);
      },
    });
  }

  getFailureTemplateIds(TemplateIds: any[], fields: string = 'templateId,alerts'){
    TemplateIds = TemplateIds.filter(t => t);
    if(TemplateIds && TemplateIds.length){
      let reqIds = TemplateIds.join(',');
      let url = `${API_URL}${API_ALERT_URL}/failure/timeline?_id=${reqIds}&getFailingTemplateIds=${1}${fields ? '&fields=' + fields : ''}`;
      this.http.get(url, {headers: this.userService.getHttpAuthHeader()}).subscribe((data: any) => {
        this.failureTemplateIdsChanged.emit(data);
      }, error => {
        let res = {
          data : []
        }
        this.failureTemplateIdsChanged.emit(res);
        console.log(error.status, error.error.message);
      })
    }
  }

  getAlertRangeAnomalyScore(table, range, c_s = 1) {
    let url = API_URL + '/api/getSystemAnomalyScore?table=' + table + '&c_s=' + c_s;
    if (range.from && range.to)
      url += '&from=' + encodeURIComponent(range.from) + '&to=' + encodeURIComponent(range.to)

    this.http.get(url, {headers: this.userService.getHttpAuthHeader()}).subscribe((data: any) => {
      let res = {
        data: data,
        left: false
      }
      this.systemDetailsService.systemAnomalyDataChanged.emit(res);
    }, error => {
      let res = {
        data: [],
        left: false
      }
      this.systemDetailsService.systemAnomalyDataChanged.emit(res);
      console.log(error.status, error.error.message);
    });
  }

  createAlert(a: AlertModel) {

    let alert = {
      title: a.title.trim(),
      description: a.description.trim(),
      type: a.type,
      severityValue: a.severityValue,
      statusValue: a.statusValue,
      state: a.state,
      owner_name: a.owner_name,
      owner : a.owner,
      facility: a.facility,
      system: a.system,
      tag: a.tag,
      trigger: a.trigger,
      range: a.range,
      validity : a.validity,
      createdTime: a.createdTime,
      confirmedTime: a.confirmedTime,
      dueDate : a.dueDate,
      component : a.component,
      errorCode : a.errorCode,
      category: a.category
    }

    return this.http.post<AlertModel>(API_URL + API_ALERT_URL, alert, { headers: this.userService.getHttpAuthHeader() })
  }

  updateAlert(a: Partial<AlertModel>, dateEnum = 0) {
    let alert = {
      _id: a._id,
      title: a.title ? a.title.trim() : undefined,
      customTitle: (a.customTitle || a.customTitle == '') ? a.customTitle.trim() : undefined,
      description: a.description ? a.description.trim() : undefined,
      type: a.type,
      bookmark: a.bookmark,
      severityValue: a.severityValue,
      statusValue: a.statusValue,
      state: a.state,
      owner_name: a.owner_name,
      owner : a.owner,
      facility: a.facility,
      system: a.system,
      tag: a.tag,
      trigger: a.trigger,
      range: a.range,
      validity : a.validity,
      createdTime: a.createdTime,
      confirmedTime: a.confirmedTime,
      dueDate : a.dueDate,
      markedFalsePositiveOn: a.markedFalsePositiveOn,
      markedOPChangeOn: a.markedOPChangeOn,
      failureAdded: a.failureAdded,
      defectId: a.defectId,
      ranges: a.ranges,
      recommendations: a.recommendations,
      userMarkedFailures: a.userMarkedFailures,
      customFailureReason: a.customFailureReason,
      failures: a.failures,
      subscribers: a.subscribers,
      reviewers: a.reviewers,
      assignedDate: a.assignedDate,
      opStateChangeTags: a.opStateChangeTags,
      lastStatusChangedOn: a.lastStatusChangedOn,
      accumulationType: a.accumulationType,
      component : a.component,
      errorCode: a.errorCode,
      category: a.category,
      catch:a.catch,
      comment:a.comment,
      totalMonetaryValue: a.totalMonetaryValue,
      evidence: a.evidence,
      restoredRcaId: a.restoredRcaId,
    }
    const options: { headers: any; params?: HttpParams } = { headers: this.userService.getHttpAuthHeader() };
    if (dateEnum) {
      options.params = new HttpParams().set('dateEnum', dateEnum);
    }
    return this.http.put<AlertModel>(API_URL + API_ALERT_URL, alert, options).pipe(
      this.splitMergeConflictService.notifyOnConflict<AlertModel>(
        () => a,
        () => Object.keys(a || {}).filter(k => k !== '_id'),
      ),
    );
  }

  updateAlertMonetaryValue(a: Partial<AlertModel>) {
    let alert = {
      _id: a._id,
      failures: a.failures
    };

    return this.http.patch<AlertModel>(API_URL + API_ALERT_MONETARY_VALUE_URL, alert, { headers: this.userService.getHttpAuthHeader() })
  }

  updateAlertFailureVisibility(alertId: string, failureIds: string[], isVisible: boolean) {
    const payload = { alertId, failureIds, isVisible };
    return this.http.put(API_URL + API_ALERT_FAILURE_VISBILITY_URL, payload, { headers: this.userService.getHttpAuthHeader() });
  }

  deleteAlert(a: AlertModel) {
    return this.http.delete<AlertModel>(API_URL + API_ALERT_URL + '?_id=' + a._id, { headers: this.userService.getHttpAuthHeader() });
  }

  markFalsePostivefeedbackAlert(params) {
    return this.http.get(this.PYTHON_API_URL + PREDICTION_FEEDBACK, { params, headers: this.userService.getHttpAuthHeader() });
  }

  updateAlertSubscibers(a) {
    let alert = a
    let url = API_ALERT_URL + '/subscribe'
    return this.http.put<AlertModel>(API_URL + url, alert, { headers: this.userService.getHttpAuthHeader() })
  }

  updateAlertTableColumns(id, tabColumnMap) {
    let param = { fields: 'alertTableColumns' }
    return this.http.put(`${API_URL}/api/user`, {
      _id: id,
      alertTableColumns: tabColumnMap
    }, { params: param, headers: this.userService.getHttpAuthHeader() })
  }

  mergeOrSplitAlert(payload: any) {
    let url = `${API_URL}/api/prediction/splitMergeAlerts`;
    return this.http.post(url, payload, { headers: this.userService.getHttpAuthHeader() }).pipe(
      tap((res: any) => this._notifySplitMergeInProgressFromResponse(res, payload)),
    );
  }

  // 202 (queued) and 409 (already in progress) both mean the source alert is
  // in the in-progress state on the server. Emit the same conflict event used
  // by updateAlert 409 — minus the snackbar, since the dialog already shows
  // the queued/conflict status. Exception: a 409 caused by the *destination*
  // alert already being in a split/merge leaves the source/current alert free,
  // so we skip locking it in that case.
  private _notifySplitMergeInProgressFromResponse(res: any, payload: any): void {
    const status = res?.data?.status || res?.status;
    if (status !== 202 && status !== 409) return;
    const isDestinationAlertLocked = res?.isDestinationAlertLocked ?? res?.data?.isDestinationAlertLocked;
    if (status === 409 && isDestinationAlertLocked) return;
    this.splitMergeConflictService.notifyInProgress(payload?.source_alert_id);
  }

  getStatusString = (statusValue: number = 0): string => {
    switch (statusValue) {
      case 1:
        return 'Unacknowledged';
      case 2:
        return 'In Process';
      case 3:
        return 'On Watch';
      case 4:
        return 'Pending - Defect Raised';
      case 5:
        return 'Closed - Maint. Deferred';
      case 6:
        return 'Closed - No Action Planned';
      case 7:
        return 'Closed - Resolved';
      case 8:
        return 'Rejected';
    }
    return '';
  }

  getSeverityString = (severityValue: number = 0): string => {
    switch (severityValue) {
      case 1:
        return 'High';
      case 2:
        return 'Medium';
      case 3:
        return 'Low';
    }
    return '';
  }

  getStateString = (state: number = 0): string => {
    switch (state) {
      case 1:
        return 'Tentative';
      case 2:
        return 'Active';
      case 3:
        return 'Returned to Normal';
      case 4:
        return 'Closed';
      case 5:
        return 'Noise/Discarded';
    }
    return '';
  }

  getAlertTypeString(type: number = 1): string {
    switch(type){
      case 1:
        return 'Anomaly';
      case 2:
        return 'Data Quality';
      case 3:
        return 'SCADA';
      case 4:
        return 'Diagnostic';
      case 5:
        return 'Threshold';
    }
  }

  getAlertCategoryString(category: number = 0): string {
    return this.alertCategoryMap[category] || '';
  }

  public getThresholdIconToolTipText(tagId: number){
    let alert = this.thresholdAlerts.find(f => f.tag.tagId === tagId);
    let lastRange = alert.ranges[alert.ranges.length - 1];
    let message = "An active threshold alert is present";
    let formattedDate = this.replaceNullValuePipe.transform(this.dateAgoPipe.transform(lastRange.from, false));
    let finalMessage = `${message} since ${formattedDate}`;
    return finalMessage;
  }

  /** Closed - Maint. Deferred / No Action / Resolved, or Rejected */
  private _isClosedOrResolvedStatus(statusValue: number): boolean {
    return CLOSED_OR_RESOLVED_STATUS_VALUES.includes(Number(statusValue));
  }

  public hasActiveNonClosedDqAlert(tagId): boolean {
    return this.dqAlerts.some(f =>
      f.tag?.tagId === tagId &&
      f.state == 2 &&
      !this._isClosedOrResolvedStatus(f.statusValue)
    );
  }

  public getActiveNonClosedDqAlert(tagId) {
    return this.dqAlerts.find(f =>
      f.tag?.tagId === tagId &&
      f.state == 2 &&
      !this._isClosedOrResolvedStatus(f.statusValue)
    );
  }

  public getDqIconToolTipText(tagId: number) {
    let alert = this.getActiveNonClosedDqAlert(tagId);

    let lastRange = alert?.ranges[alert.ranges?.length - 1];
    let subtype = lastRange?.subtype !== undefined ? lastRange?.subtype : alert?.subtype;
    let message = '';

    switch (subtype) {
      case 1:
        message = "There's been an Outlier issue";
        break;
      case 2:
        message = "This tag has been experiencing an Out-Of-Range issue";
        break;
      case 4:
        message = "This tag has been experiencing a Flatline";
        break;
      case 8:
        message = "There's been a Missing Data problem with this tag";
        break;
      case 10:
        message = "There's been a paint brushing issue with this tag";
        break;
      default:
        message = "There has been an unidentified issue with this tag";
    }

    if (lastRange?.from) {
      let formattedDate = this.replaceNullValuePipe.transform(this.dateAgoPipe.transform(lastRange.from, false));
      let finalMessage = `${message} since ${formattedDate}`;
      return finalMessage;
    }
    else {
      return message;
    }
  }

  openAnalysis(alert, user, data, mode, groupBy = '', groupCount = 4, range: {to: any, from: any} = null) {
    const analysis = mode === 'iow' ? this.createAnalysisObjectForIOW(user, data, groupBy, groupCount, range) : this.createAnalysisObject(alert, user, data, mode);
    const url = location.origin + '/web/analysis?tab=AdvancedAnalysis&exportView=1' + `${mode === 'iow' ? '&iow=1' : ''}`;
    if (sessionStorage.getItem("analysisItem")) {
      sessionStorage.removeItem("analysisItem");
    }
    sessionStorage.setItem("analysisItem", JSON.stringify(analysis));
    window.open(url, '_blank');
  }

  createAnalysisObject(alert, user, data, mode) {
    let contributingTagsObjectArray, name;
    const alertConfirmedDate = moment(alert.createdTime);
    const currentDate = moment();
    const fromDate = alertConfirmedDate.clone().subtract(30, 'days');
    const toDate = moment.min(alertConfirmedDate.clone().add(5, 'days'), currentDate);
    const formattedFromDate = fromDate.toISOString();
    const formattedToDate = toDate.toISOString();
    const date = new Date();
    const formattedDate = moment(date).format('DD-MMM-YYYY, h:mm A');
    `IOW Analysis - ${formattedDate} - ${user.name}`
    if(mode == 'failureMode') {
      name =`${alert.system.name} ${data.name} - ${formattedDate} - ${user.name}`
      const contributingTags = alert.failures.find(failure => failure.mode === data._id)?.tags;
      contributingTagsObjectArray = data.tags?.filter((tag: any) => contributingTags.includes(tag.t._id));
    } else {
      name = `${alert.system.name} - ${formattedDate} - ${user.name}`;
      contributingTagsObjectArray = data
    }
    const analysis = {
      "public": false,
      "name": name,
      "facility": alert.facility,
      "failure": data._id ? data._id : '',
      "contributingTags": data._id ? [] : contributingTagsObjectArray?.map(tag=>tag._id),
      "modifiedTime": new Date(),
      "createdTime": new Date(),
      "alert": alert._id,
      "user": {
        "_id": user._id,
        "name": user.name,
        "email": user.email
      },
      "description": `${alert.system.name} - ${name} Trend of Contributing Tag`,
      "analysisItems": []
    }

    if (contributingTagsObjectArray?.length) {
      contributingTagsObjectArray.forEach(tag => {
        const from = formattedFromDate,
          to = formattedToDate
        let _id, tagId, tagRef;
        if(mode == 'failureMode') {
          tagRef = tag.t
          _id = tag.t._id
          tagId = tag.t.tagId
        } else {
          tagRef = tag
          _id = tag._id
          tagId = tag.tagId
        }

        // handle tags from different systems
        const tagOwnSystem = tagRef?.system?.[0];
        const isCrossSystem = !!(tagOwnSystem?._id && tagOwnSystem._id !== alert.system._id);
        const resolvedSystem = isCrossSystem ? tagOwnSystem : alert.system;
        const tsTable = resolvedSystem?.tsTable;
        const enableTagMongoIdFormat = resolvedSystem?.enableTagMongoIdFormat;
        const resolvedSystemId = resolvedSystem?._id;
        const resolvedUnitId = isCrossSystem ? tagOwnSystem?.unit?._id : alert?.unit?._id;

        if (!_id || !tagId) return;
        let currentAnalysisIndex = analysis.analysisItems.length - 1;
        if (analysis.analysisItems.length === 0 || analysis.analysisItems[currentAnalysisIndex].graph.length >= 8) {
          const newAnalysisItem = {
            "filter": {
              "dataFilter": {
                "min": null,
                "max": null,
                "tag": null
              },
              "units": [
                resolvedUnitId
              ],
              "systems": [
                resolvedSystemId
              ],
              "assets": [],
              "filter": [],
              "isShow": true
            },
            "plotType": -1,
            "lockXAxis": true,
            "multiAxis": false,
            "xaxis": [],
            "yaxis": [],
            "analysisType": 1, // signal vs time
            "name": `${alert.system.name} - ${name}${analysis.analysisItems.length >= 1 ? ' Part ' + (analysis.analysisItems.length + 1) : ''}`,
            "description": `${alert.system.name} - ${name}: Trend of contributing tag${analysis.analysisItems.length >= 1 ? ' Part ' + (analysis.analysisItems.length + 1) : ''}`,
            "graph": []
          }
          analysis.analysisItems.push(newAnalysisItem);
          currentAnalysisIndex++;
        }
        //include other systems and units in the filter if not already present
        if (resolvedSystemId && !analysis.analysisItems[currentAnalysisIndex].filter.systems.includes(resolvedSystemId)) {
          analysis.analysisItems[currentAnalysisIndex].filter.systems.push(resolvedSystemId);
        }
        if (resolvedUnitId && !analysis.analysisItems[currentAnalysisIndex].filter.units.includes(resolvedUnitId)) {
          analysis.analysisItems[currentAnalysisIndex].filter.units.push(resolvedUnitId);
        }
        const currentGraphObject = {
          "axis1": {
            "t": enableTagMongoIdFormat ? _id : tagId, //tagId
            "tag": _id,
            "tsTable": tsTable,
            "raw": 0
          },
          "axis2": {
            "t": "",
            "tag": null,
            "tsTable": "",
            "raw": 0
          },
          "range": {
            "from": from,
            "to": to
          },
          "xaxis": [],
          "yaxis": [],
          "raw": 0
        }
        analysis.analysisItems[currentAnalysisIndex].graph.push(currentGraphObject)
      })
    }
    return analysis;
  }

  groupTagsBy(array, keyFn: (item) => string | undefined) {
    const map = new Map<string, any>();
    const falsyKey = "falsy"; // The key used for falsy values
    const falsyItems = []; // Collect all falsy items separately

    array.forEach(item => {
      const key = keyFn(item);
      if (!key) {
        falsyItems.push(item); // Add to falsy items if the key is falsy
      } else {
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(item);
      }
    });

    if (falsyItems.length > 0) {
      map.set(falsyKey, falsyItems); // Add falsy items under the "falsy" key
    }

    return map;
  }

  sortGroupedTags(
    map: Map<string, any[]>,
    sortByFn: (a: any, b: any, key: string) => number,
    sortBy: string,
    asc: boolean = true
  ): Map<string, any[]> {
    const sortedMap = new Map<string, any[]>();
    map.forEach((value, key) => {
      const sortedArray = value.sort((a, b) => {
        const order = asc ? 1 : -1;
        return sortByFn(a, b, sortBy) * order;
      });
      sortedMap.set(key, sortedArray);
    });
    return sortedMap;
  }

  customSortForTag(a: any, b: any, key: string) {
    const isAFalsy = !a[key];
    const isBFalsy = !b[key];

    if (isAFalsy && !isBFalsy) return 1;
    if (!isAFalsy && isBFalsy) return -1;
    return 0;
  }

  createAnalysisObjectForIOW(user, tagsData, groupBy, groupCount, range) {
    if(!tagsData?.length) return;
    const facility = tagsData.find(element => element.system?.facility?._id).system.facility._id;
    const iowTags = tagsData.map(tag => tag._id);
    const date = new Date();
    const formattedDate = moment(date).format('DD-MMM-YYYY, h:mm A');
    const analysisTitle = `IOW Analysis - ${formattedDate} - ${user.name}`
    let from = moment().subtract(7, 'days').toISOString();
    let to = moment().toISOString();
    if(range.from) {
      from = moment(range.from).toISOString();
    }
    if(range.to) {
      to = moment(range.to).toISOString();
    }
    let groupedTags = new Map<string, any>();
    if(groupBy === 'UOM') {
     groupedTags = this.groupTagsBy(tagsData, item => item.unit);
     groupedTags = this.sortGroupedTags(groupedTags, this.customSortForTag, 'type', true); // so that elements with undefined values go to the bottom
    }
    else {
      // defaults on system
      groupedTags =  this.groupTagsBy(tagsData, item => item.system._id);
    }
    const analysis = {
      "public": false,
      "name": analysisTitle,
      "facility": facility,
      "failure": '',
      "contributingTags": iowTags,
      "modifiedTime": new Date(),
      "createdTime": new Date(),
      "alert": '',
      "user": {
        "_id": user._id,
        "name": user.name,
        "email": user.email
      },
      "description": `IOW Trends grouped by ${groupBy === 'UOM' ? 'UoM' : 'System'}`,
      "analysisItems": []
    }
    this.addAnalysisItemsGroupFromMap(analysis, groupedTags, from, to, groupBy, groupCount)
    return analysis;
  }

  addAnalysisItemsGroupFromMap(analysis: any, groupedTags: Map<string, any>, from: any, to: any, groupBy = '', groupCount = 4) {
    const tagDataEntries = Array.from(groupedTags.entries());
    for (const [key, value] of tagDataEntries) {
      let terminateCurrentAnalysis = true
      value.forEach(tag => {
        const tsTable = tag.system.tsTable,
        enabledMongoFormat = tag.system.enableTagMongoIdFormat,
        tagId = tag.tagId,
        _id = tag._id;
        if(!_id || !tagId) return;
        let currentAnalysisIndex = analysis.analysisItems.length - 1;
        if (terminateCurrentAnalysis || analysis.analysisItems.length === 0 || analysis.analysisItems[currentAnalysisIndex].graph.length >= groupCount) {
          terminateCurrentAnalysis = false;
          let analysisName = `${tag.system.unit.name} / ${tag.system.name}`;
          let analysisDescription = `Trends Grouped By ${tag.system.name}`;
          if(groupBy === 'UOM') {
            if(!tag.unit) {
              analysisName = "Trends with undefined UoM"
              analysisDescription = "IOW Trends grouped by tags with undefined UoM"
            }
            else if(tag.unit && !tag.type) {
              analysisName = `Trends with UoM - ${tag.unit}`
            }
            else {
              analysisName = `${tag.type} - ${tag.unit}`;
              analysisDescription = `Trends grouped by ${tag.unit}`
            }
          }
          const newAnalysisItem = {
            "filter": {
              "dataFilter": {
                "min": null,
                "max": null,
                "tag": null
              },
              "units": [tag.system.unit._id],
              "systems": [tag.system._id],
              "assets": [],
              "filter": [],
              "isShow": true
            },
            "plotType": -1,
            "lockXAxis": true,
            "multiAxis": false,
            "xaxis": [],
            "yaxis": [],
            "analysisType": 1, // signal vs time
            "name": analysisName,
            "description": analysisDescription,
            "graph": []
          }
          analysis.analysisItems.push(newAnalysisItem);
          currentAnalysisIndex++;
        }

        if (groupBy && !analysis.analysisItems[currentAnalysisIndex].filter.units.includes(tag.system.unit._id)) {
          analysis.analysisItems[currentAnalysisIndex].filter.units.push(tag.system.unit._id);
        }

        if (groupBy && !analysis.analysisItems[currentAnalysisIndex].filter.systems.includes(tag.system._id)) {
          analysis.analysisItems[currentAnalysisIndex].filter.systems.push(tag.system._id);
        }
        const currentGraphObject = {
          "axis1": {
            "t": enabledMongoFormat ? _id : tagId,
            "tag": _id,
            "tsTable": tsTable,
            "raw": tag.calculated ? 0 : 1
          },
          "axis2": {
            "t": "",
            "tag": null,
            "tsTable": "",
            "raw": tag.calculated ? 0 : 1
          },
          "range": {
            "from": from,
            "to": to
          },
          "xaxis": [],
          "yaxis": [],
          "raw": tag.calculated ? 0 : 1
        }
        analysis.analysisItems[currentAnalysisIndex].graph.push(currentGraphObject)
      })
    }
  }

  getGeneratedRecommendations(params) {
    let url = API_URL + '/api/alertrca/list'
    return this.http.post(url, params, { headers: this.userService.getHttpAuthHeader() })
  }

  deleteGeneratedRecommendations(params) {
    let url = API_URL  + '/api/alertrca'
    return this.http.delete(url, {params, headers: this.userService.getHttpAuthHeader() })
  }

  createAlertRecommendations(params: any) {
    const url = API_URL + '/api/alertrca';
    return this.http.post(url, params, { headers: this.userService.getHttpAuthHeader() });
  }
  regenerateAlertRecommendationsFromFeedback(body: any) {
    const url = API_URL + '/api/alertrca-regen';
    return this.http.post(url, body, { headers: this.userService.getHttpAuthHeader() });
  }

  /**
   * Requests an RCA report export.
   *
   * Generation is minutes-scale on tag-heavy alerts, so it never runs inline and
   * never streams a PDF back: the request is always queued (202) and the finished
   * report is delivered out of band — by email for `download`, onto the discussion
   * comment for `share`.
   */
  requestRcaReport(payload: AlertRcaReportRequest): Observable<HttpResponse<AlertRcaReportResponse>> {
    const url = API_URL + '/api/alertrca-report';
    return this.http.post<AlertRcaReportResponse>(
      url,
      payload, {
        headers: this.userService.getHttpAuthHeader(),
        observe: 'response'
      }
    )
  }

  getSharedRcaReport(alertLogId: string): Observable<AlertRcaReportStatus> {
    const url = `${API_URL}/api/alertrca-report/${alertLogId}`;
    return this.http.get<AlertRcaReportStatus>(url, { headers: this.userService.getHttpAuthHeader() });
  }

  validateSapNotif(body: {notificationNo: string, functionalLoc: string[], originalNotificationNo?:string}) {
    const url = `${API_URL}${API_ALERT_URL}/validate-sap-notification`;
    return this.http.post(url, body, { headers: this.userService.getHttpAuthHeader() });
  }

  addSapNotificationToAlertFailure(body: {alertId: string, failureId: string, newNotif: {id: string, notificationNo: string} , originalNotificationNo?:string, log: boolean}) {
    const url = `${API_URL}${API_ALERT_URL}/add-alert-failure-sap-notification`;
    return this.http.put(url, body, { headers: this.userService.getHttpAuthHeader() });
  }

  deleteSapNotificationFromAlertFailure(body: {alertId: string, failureId: string, notificationNo?: string, removeAll?: boolean}) {
    const url = `${API_URL}${API_ALERT_URL}/remove-alert-failure-sap-notification`;
    return this.http.put(url, body, { headers: this.userService.getHttpAuthHeader() });
  }

  updateAlertRecommendations(alertData:any){
    const url = API_URL + '/api/alertrca';
    return this.http.put(url, alertData, { headers: this.userService.getHttpAuthHeader() });
  }

  getAnalysisListForAlert(payload, fields) {
    let url = API_URL + API_ANALYSIS_ALERT_URL;
    let params = new HttpParams();
    if (fields) {
      params = params.set('fields', fields);
    }
    return this.http.post(url, payload, { headers: this.userService.getHttpAuthHeader(), params })
  }

  getAlertViewList(payload, fields) {
    if (fields) {
      payload.fields = fields.join(',');
    }
    return this.http.post(`${this.alertViewUrl}/list`, payload, { headers: this.userService.getHttpAuthHeader() })
  }

  saveAlertView(view: AlertViewModel) {
    return this.http.post(this.alertViewUrl, view, { headers: this.userService.getHttpAuthHeader() });
  }

  updateAlertView(view: any) {
    return this.http.put(this.alertViewUrl, view, { headers: this.userService.getHttpAuthHeader() });
  }

  deleteAlertView(_id: string) {
    return this.http.delete(this.alertViewUrl, { headers: this.userService.getHttpAuthHeader(), params: new HttpParams().set('_id', _id) });
  }
}
